/**
 * Priorita come SIMBOLO nella coda a tabella (veste «A · Tabella pulita»):
 * forma e colore diversi per ciascun livello, cosi si distinguono anche senza
 * colori. Urgente ⇈ rosso, Alta ↑ arancio, Media = grigio, Bassa ↓ blu.
 * Colori in support-v3.css (`--s3-prio-*`, varianti per .dark con contrasto
 * non testuale >= 3:1). La card e la scheda restano sulle barre (`PriorityBars`).
 */
import React from 'react';
import { ChevronDown, ChevronUp, ChevronsUp, Equal, type LucideIcon } from 'lucide-react';
import { PRIORITY_LABELS } from '../constants';

const ICONS: Record<string, LucideIcon> = {
  urgente: ChevronsUp,
  alta: ChevronUp,
  media: Equal,
  bassa: ChevronDown,
};

/** Ordine della legenda: dalla piu alta alla piu bassa. */
export const PRIORITY_LEGEND = ['urgente', 'alta', 'media', 'bassa'] as const;

const labelOf = (priority: string): string => PRIORITY_LABELS[priority] || priority;

export interface PriorityIconProps {
  priority: string;
  /** Accanto c'e gia il nome (legenda): simbolo decorativo. */
  decorative?: boolean;
  size?: number;
}

const PriorityIcon: React.FC<PriorityIconProps> = ({ priority, decorative = false, size = 18 }) => {
  const Icon = ICONS[priority] || Equal;
  const name = `Priorità ${labelOf(priority).toLowerCase()}`;
  const a11y = decorative
    ? { 'aria-hidden': true as const }
    : { role: 'img' as const, 'aria-label': name, title: name };
  return (
    <span className={`s3-pico s3-pico--${ICONS[priority] ? priority : 'media'}`} data-priority={priority} {...a11y}>
      <Icon size={size} strokeWidth={2.75} aria-hidden="true" focusable="false" />
    </span>
  );
};

/** Legenda dei quattro simboli col nome (riga della legenda sopra la tabella). */
export const PriorityLegend: React.FC = () => (
  <span className="s3-plegend" role="group" aria-label="Legenda della priorità">
    {PRIORITY_LEGEND.map((p) => (
      <span key={p} className="s3-plegend__item">
        <PriorityIcon priority={p} decorative size={14} />
        {labelOf(p)}
      </span>
    ))}
  </span>
);

export default PriorityIcon;
